/**
 * Post Create Handler
 *
 * Coordinates create-form media, ratings, translation, and preview behavior.
 */

import { PostCreateApiService } from "./post-create-api";
import type { ImagePreviewData, PostCreateJsonValue, VideoPreviewData } from "./post-create-api";
import { notifier } from "../../core/notifier/notifier";
import { translator } from "../../translation/translator";
import { VideoUtils } from "../../util/media/video/video-utils";
import { SNSPreviewManagerSetup } from "../../sns/preview-manager/sns-preview-manager-setup";
import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { PostMediaRender } from "../media/post-media-render";
import { PostSharedPreviewTabUtils } from "../shared/post-shared-preview-tab-utils";
import { renderMediaTiles, renderRatingStarsPreview } from "./post-create-render";
import { postCreateForm } from "./post-create-form";
import type { PostCreateImageItem, PostCreateVideoItem } from "./post-create-form";
import { PostCreateListener } from "./post-create-listener";
import { PostCreateUtils } from "./post-create-utils";
import { CommonUtils } from "../../util/common-utils";
import type { ImageHandler } from "../../util/media/image/image-handler";

interface RatingData {
    average: number;
    count: number;
}

interface PostCreateFormManagerLike {
    addImages: (images: PostCreateImageItem[]) => void;
    addVideos: (videos: PostCreateVideoItem[]) => void;
    removeImage: (imageId: number) => void;
    removeVideo: (videoId: number) => void;
    getImages: () => PostCreateImageItem[];
    getVideos: () => PostCreateVideoItem[];
    setTranslationEnabled: (enabled: boolean) => void;
    setRating: (rating: number) => void;
    setTitle: (title: string) => void;
    setContent: (content: string) => void;
}

interface PostCreateElements {
    form?: HTMLFormElement | null;
    imageList?: HTMLElement | null;
    videoList?: HTMLElement | null;
    ratingSlider?: HTMLInputElement | null;
    titleInput?: HTMLInputElement | HTMLTextAreaElement | null;
    contentTextarea?: HTMLTextAreaElement | null;
    [key: string]: PostCreateJsonValue | Element | null | undefined;
}

class PostCreateHandler {
    public imageUploadHandler: ((event: Event) => void | Promise<void>) | null;
    public videoUploadHandler: ((event: Event) => void | Promise<void>) | null;
    public previewHandler: ((event: Event) => void) | null;
    public translationToggleHandler: ((event: Event) => void) | null;
    public ratingChangeHandler: ((event: Event) => void) | null;
    public starClickHandler: ((event: Event) => void) | null;
    public starHoverHandler: ((event: Event) => void) | null;
    public ratingMouseLeaveHandler: ((event: Event) => void) | null;
    public starKeydownHandler: ((event: Event) => void) | null;
    public imageManager: ImageHandler | null;
    public titleChangeHandler: ((event: Event) => void) | null;
    public contentChangeHandler: ((event: Event) => void) | null;
    public elements: PostCreateElements;
    public formManager: PostCreateFormManagerLike | null;
    public apiService: PostCreateApiService;

    public constructor() {
        this.imageUploadHandler = this.createHandlerSlot();
        this.videoUploadHandler = this.createHandlerSlot();
        this.previewHandler = this.createHandlerSlot();
        this.translationToggleHandler = this.createHandlerSlot();
        this.ratingChangeHandler = this.createHandlerSlot();
        this.starClickHandler = this.createHandlerSlot();
        this.starHoverHandler = this.createHandlerSlot();
        this.ratingMouseLeaveHandler = this.createHandlerSlot();
        this.starKeydownHandler = this.createHandlerSlot();
        this.imageManager = this.createImageManagerSlot();
        this.titleChangeHandler = this.createHandlerSlot();
        this.contentChangeHandler = this.createHandlerSlot();

        this.elements = {};
        this.formManager = null;
        this.apiService = new PostCreateApiService();
    }

    public createHandlerSlot(): null {
        return null;
    }

    public createImageManagerSlot(): null {
        return null;
    }

    public async handleImageUpload(event: Event): Promise<void> {
        if (this.imageManager) {
            return;
        }

        const files = this.getFilesFromEvent(event);
        if (files.length === 0) return;

        try {
            const config = this.apiService.getConfig();
            const validFiles = files.filter((file) => {
                return config.allowedImageTypes.includes(file.type) && file.size <= config.maxImageSize;
            });

            if (validFiles.length !== files.length) {
                notifier.warn("post_create_files_skipped_invalid_type_or_size");
            }

            const processedImages: ImagePreviewData[] = [];
            for (const file of validFiles) {
                try {
                    const preview = await this.apiService.generateImagePreview(file);
                    processedImages.push(preview);
                } catch (error) {
                    console.error("Failed to process image:", file.name, error);
                }
            }

            if (processedImages.length > 0 && this.formManager) {
                this.formManager.addImages(processedImages);
                this.updateImageDisplay();
            }
        } catch (error) {
            console.error(error);
            notifier.error("post_create_process_images_failed");
        }

        const target = event.target;
        if (target instanceof HTMLInputElement) {
            target.value = "";
        }
    }

    public async handleVideoUpload(event: Event): Promise<void> {
        const files = this.getFilesFromEvent(event);
        if (files.length === 0) return;

        try {
            const config = this.apiService.getConfig();
            const validFiles: File[] = [];

            for (const file of files) {
                const validation = VideoUtils.validateVideo(file, {
                    allowedTypes: config.allowedVideoTypes,
                    maxSize: config.maxVideoSize,
                });

                if (validation.isValid) {
                    validFiles.push(file);
                }
            }

            if (validFiles.length !== files.length) {
                notifier.warn("post_create_files_skipped_invalid_type_or_size");
            }

            const processedVideos: VideoPreviewData[] = [];
            for (const file of validFiles) {
                // Thumbnail generation uses the original file while the content:// URI is still
                // valid (immediately after selection) — same as edit mode (video-setup.ts).
                // ArrayBuffer-backed blobs (fileToMemory result) can confuse Android's video decoder.
                // fileToMemory is applied AFTER thumbnail generation so the form submission File
                // is safely in-memory by the time the user fills out and submits the form.
                try {
                    const preview = await this.apiService.generateVideoPreview(file);
                    const inMemFile = await PostCreateUtils.fileToMemory(file);
                    processedVideos.push({ ...preview, file: inMemFile });
                } catch (_error) {
                    // Thumbnail generation failed; add video without thumbnail.
                    const inMemFile = await PostCreateUtils.fileToMemory(file);
                    processedVideos.push({
                        id: Date.now() + Math.random(),
                        file: inMemFile,
                        name: inMemFile.name,
                        size: inMemFile.size,
                        duration: 0,
                        previewUrl: "",
                        videoUrl: "",
                        url: "",
                    });
                }
            }

            if (processedVideos.length > 0 && this.formManager) {
                this.formManager.addVideos(processedVideos);
                this.updateVideoDisplay();
            }
        } catch (error) {
            console.error(error);
            notifier.error("post_create_process_videos_failed");
        }

        const target = event.target;
        if (target instanceof HTMLInputElement) {
            target.value = "";
        }
    }

    public async handleDroppedFiles(files: File[]): Promise<void> {
        const images: File[] = [];
        const videos: File[] = [];

        files.forEach((file) => {
            if (file.type.startsWith("image/")) {
                images.push(file);
            } else if (file.type.startsWith("video/")) {
                videos.push(file);
            }
        });

        if (images.length > 0) {
            await this.handleImageUpload(this.createFileEvent(images));
        }

        if (videos.length > 0) {
            await this.handleVideoUpload(this.createFileEvent(videos));
        }
    }

    public handlePreview(event: Event): void {
        event.preventDefault();

        try {
            this.updatePreviewContent();
        } catch (error) {
            console.error("Failed to generate preview:", error);
            notifier.error("post_create_preview_failed");
        }
    }

    public updatePreviewContent(): void {
        const formRoot =
            postCreateForm.getPostCreateForm(this.elements?.form) || postCreateForm.getPostCreateForm(document);
        const root = formRoot || document;

        const previewContainer = postCreateForm.getPreviewRoot(root);
        if (!previewContainer || !formRoot) {
            return;
        }

        const { previewBody } = PostSharedPreviewTabUtils.ensureRichHost(previewContainer);
        this.removeExistingPreviewElements(previewBody);

        let html = "";
        let contentText = "";

        const quillRoot = postCreateForm.getWysiwygContent(root);
        const contentTextarea = postCreateForm.getContentField(root);

        if (quillRoot && quillRoot.innerHTML.trim()) {
            html = quillRoot.innerHTML;
            contentText = quillRoot.textContent || quillRoot.innerText || "";
        } else if (contentTextarea && contentTextarea.value.trim()) {
            contentText = contentTextarea.value;
            html = contentText.replace(/\n/g, "<br>");
        }

        const snsInputs = postCreateForm.getSnsLinkInputs(root);
        const snsUrls = SNSLinksUtils.normalizeSnsLinks(
            Array.from(snsInputs).map((element) => (element instanceof HTMLInputElement ? element.value.trim() : "")),
        );

        const placeholder = this.getTranslation("content_placeholder", "เนื้อหาจะแสดงที่นี่...");

        PostSharedPreviewTabUtils.updatePreviewTab({
            formRoot,
            previewRoot: previewContainer,
            html,
            snsUrls,
            placeholderText: placeholder,
            afterRichRender: ({ richHost }: { richHost: HTMLElement }) => {
                this.addSNSPreview(contentText, richHost);
            },
        });

        try {
            const { previewBody: ensuredPreviewBody, richHost } =
                PostSharedPreviewTabUtils.ensureRichHost(previewContainer);
            PostSharedPreviewTabUtils.renderReviewQuestionsPreview({
                formRoot,
                previewBody: ensuredPreviewBody,
                richHost,
            });
        } catch (_error) {
            void _error;
        }

        this.updateOgpPreviewInTab();
    }

    public removeExistingPreviewElements(previewBody: HTMLElement): void {
        postCreateForm.getPreviewCleanupElements(previewBody).forEach((element) => element.remove());
    }

    public addSNSPreview(contentText: string, richHost: HTMLElement | null): void {
        if (!contentText || !(richHost instanceof HTMLElement)) return;

        try {
            const snsPreviewManager = SNSPreviewManagerSetup.getManager() as {
                processContentForDisplay?: (root: HTMLElement) => void;
            } | null;
            if (snsPreviewManager && typeof snsPreviewManager.processContentForDisplay === "function") {
                snsPreviewManager.processContentForDisplay(richHost);
            }
        } catch (error) {
            console.error("SNS preview processing failed:", error);
        }
    }

    public handleTranslationToggle(event: Event): void {
        const enabled = event.target instanceof HTMLInputElement ? event.target.checked : false;
        this.formManager?.setTranslationEnabled(enabled);
        this.updateTranslationUI(enabled);
    }

    public handleRatingChange(event: Event): void {
        const rating = event.target instanceof HTMLInputElement ? parseInt(event.target.value, 10) : 0;
        this.formManager?.setRating(rating);
        this.updateRatingDisplay(rating);
    }

    public handleStarClick(event: Event): void {
        const starIcon = postCreateForm.getRatingStar(event);

        if (!(starIcon instanceof HTMLElement)) return;

        event.preventDefault();
        event.stopPropagation();

        const rating = parseInt(starIcon.getAttribute("data-value") || "0", 10);
        const ratingInput = postCreateForm.getRatingInput(starIcon);
        if (!(ratingInput instanceof HTMLElement)) return;

        const hiddenInput = postCreateForm.getReviewAnswerInput(ratingInput);
        if (hiddenInput instanceof HTMLInputElement) {
            hiddenInput.value = String(rating);
            hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));
            hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
        }

        const stars = postCreateForm.getRatingStars(ratingInput);
        this.updateStarDisplay(stars, rating);

        starIcon.classList.add("just-selected");
        setTimeout(() => {
            starIcon?.classList.remove("just-selected");
        }, 500);

        this.formManager?.setRating(rating);
        if (this.elements.ratingSlider instanceof HTMLInputElement) {
            this.elements.ratingSlider.value = String(rating);
        }

        this.updateAverageRating();
    }

    public handleStarHover(event: Event): void {
        const starIcon = postCreateForm.getRatingStar(event);

        if (!(starIcon instanceof HTMLElement)) return;

        const stars = postCreateForm.getRatingStars(postCreateForm.getRatingInput(starIcon));
        if (!stars) return;

        const rating = parseInt(starIcon.getAttribute("data-value") || "0", 10);
        this.highlightStars(stars, rating);
    }

    public handleRatingMouseLeave(event: Event): void {
        const ratingInput = postCreateForm.getRatingInput(event);
        if (!(ratingInput instanceof HTMLElement)) return;

        const hiddenInput = postCreateForm.getReviewAnswerInput(ratingInput);
        const currentRating = hiddenInput instanceof HTMLInputElement ? parseInt(hiddenInput.value, 10) || 0 : 0;
        const stars = postCreateForm.getRatingStars(ratingInput);

        this.updateStarDisplay(stars, currentRating);
    }

    public handleStarKeydown(event: Event): void {
        if (!(event instanceof KeyboardEvent)) return;
        const starIcon = postCreateForm.getRatingStar(event);

        // Resolve the .review-question regardless of whether a star or the question itself is focused
        const reviewQuestion = starIcon instanceof HTMLElement
            ? postCreateForm.getReviewQuestion(starIcon)
            : postCreateForm.getReviewQuestionAsEventTarget(event);

        if (!(reviewQuestion instanceof HTMLElement)) return;

        // Resolve the rating group: from star (closest parent) or from question (first child)
        const ratingInput = starIcon instanceof HTMLElement
            ? postCreateForm.getRatingInput(starIcon)
            : postCreateForm.getRatingInputFromQuestion(reviewQuestion);

        const stars = ratingInput instanceof HTMLElement
            ? postCreateForm.getRatingStars(ratingInput)
            : [];

        // ArrowLeft/Right: navigate within the star group (only when a star has focus)
        if (event.key === "ArrowRight") {
            event.preventDefault();
            if (starIcon instanceof HTMLElement && ratingInput instanceof HTMLElement) {
                const nextStar = stars[stars.indexOf(starIcon) + 1];
                if (nextStar instanceof HTMLElement) {
                    this.commitStarRating(ratingInput, stars, parseInt(nextStar.getAttribute("data-value") || "0", 10));
                    nextStar.focus({ preventScroll: true });
                }
            }
            return;
        }

        if (event.key === "ArrowLeft") {
            event.preventDefault();
            if (starIcon instanceof HTMLElement && ratingInput instanceof HTMLElement) {
                const prevStar = stars[stars.indexOf(starIcon) - 1];
                if (prevStar instanceof HTMLElement) {
                    this.commitStarRating(ratingInput, stars, parseInt(prevStar.getAttribute("data-value") || "0", 10));
                    prevStar.focus({ preventScroll: true });
                }
            }
            return;
        }

        // ArrowDown/Up: move focus to adjacent .review-question
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const adjacent = event.key === "ArrowDown"
                ? postCreateForm.getNextReviewQuestion(reviewQuestion)
                : postCreateForm.getPreviousReviewQuestion(reviewQuestion);
            if (adjacent instanceof HTMLElement) {
                this.focusReviewQuestion(adjacent);
            }
            return;
        }

        // Number keys 1–5: set rating and advance to next .review-question
        const digit = parseInt(event.key, 10);
        if (isNaN(digit) || digit < 1 || digit > 5) return;
        if (!(ratingInput instanceof HTMLElement)) return;

        event.preventDefault();
        this.commitStarRating(ratingInput, stars, digit);

        const nextQuestion = postCreateForm.getNextReviewQuestion(reviewQuestion);
        if (nextQuestion instanceof HTMLElement) {
            this.focusReviewQuestion(nextQuestion);
        } else {
            this.focusNextAfterReviews(reviewQuestion);
        }
    }

    private focusNextAfterReviews(reviewQuestion: HTMLElement): void {
        const form = postCreateForm.getClosestForm(reviewQuestion);
        if (!(form instanceof HTMLElement)) return;

        // Prefer the Quill editor contenteditable div
        const qlEditor = postCreateForm.getWysiwygContentEditor(form);
        if (qlEditor instanceof HTMLElement) {
            qlEditor.focus();
            return;
        }

        // Fallback: visible content textarea or title input
        const contentField = postCreateForm.getContentField(form as ParentNode);
        if (contentField instanceof HTMLTextAreaElement) {
            contentField.focus();
            return;
        }

        const titleField = postCreateForm.getTitleField(form as ParentNode);
        if (
            (titleField instanceof HTMLInputElement && titleField.type !== "hidden") ||
            titleField instanceof HTMLTextAreaElement
        ) {
            titleField.focus();
        }
    }

    private focusReviewQuestion(el: HTMLElement): void {
        if (!el.hasAttribute("tabindex")) {
            el.setAttribute("tabindex", "-1");
        }
        el.focus({ preventScroll: true });
    }

    private commitStarRating(ratingInput: HTMLElement, stars: HTMLElement[], rating: number): void {
        const hiddenInput = postCreateForm.getReviewAnswerInput(ratingInput);
        if (hiddenInput instanceof HTMLInputElement) {
            hiddenInput.value = String(rating);
            hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));
            hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
        this.updateStarDisplay(stars, rating);
        this.formManager?.setRating(rating);
    }

    public handleTitleChange(event: Event): void {
        const title =
            event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement
                ? event.target.value
                : "";
        this.formManager?.setTitle(title);
    }

    public handleContentChange(event: Event): void {
        const content = event.target instanceof HTMLTextAreaElement ? event.target.value : "";
        this.formManager?.setContent(content);
    }

    public updateStarDisplay(stars: NodeListOf<Element> | Element[], rating: number): void {
        Array.from(stars).forEach((star, index) => {
            if (!(star instanceof HTMLElement)) return;
            star.classList.toggle("selected", index < rating);
            star.classList.remove("hover-highlight");
        });
    }

    public highlightStars(stars: NodeListOf<Element> | Element[], rating: number): void {
        Array.from(stars).forEach((star, index) => {
            if (!(star instanceof HTMLElement)) return;
            star.classList.remove("selected", "hover-highlight");
            if (index < rating) {
                star.classList.add("hover-highlight");
            }
        });
    }

    public updateAverageRating(): void {
        const ratingData = this.calculateAverageRating();
        this.renderStars(ratingData.average);
        this.updateRatingElements(ratingData);
    }

    public calculateAverageRating(): RatingData {
        const starInputs = postCreateForm.getAllRatingAnswerInputs(document);
        let total = 0;
        let count = 0;

        starInputs.forEach((input) => {
            const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) || 0 : 0;
            if (value > 0) {
                total += value;
                count++;
            }
        });

        return {
            average: count > 0 ? total / count : 0,
            count,
        };
    }

    public renderStars(rating: number): void {
        const container = postCreateForm.getUserStarsPreview(document);
        if (!(container instanceof HTMLElement)) return;

        renderRatingStarsPreview(container, rating);
    }

    public updateRatingElements(ratingData: RatingData): void {
        const averageElement = postCreateForm.getUserRatingAverage(document);
        const countElement = postCreateForm.getUserRatingCount(document);

        if (averageElement instanceof HTMLElement) {
            averageElement.textContent =
                ratingData.count === 0 ? "-" : CommonUtils.formatAverageRating(ratingData.average, 2, "-");
        }

        if (countElement instanceof HTMLElement) {
            const currentLang = translator?.getCurrentLanguage?.() || "th";
            const translations = {
                th: ratingData.count === 0 ? "ยังไม่ได้ให้คะแนน" : `(เฉลี่ย ${ratingData.count} รายการ)`,
                en: ratingData.count === 0 ? "Not rated yet" : `(Average of ${ratingData.count} items)`,
                ja: ratingData.count === 0 ? "未評価" : `(${ratingData.count} 件の平均)`,
            };
            countElement.textContent = translations[currentLang as keyof typeof translations] || translations.th;
        }
    }

    public updateImageDisplay(): void {
        if (!(this.elements.imageList instanceof HTMLElement) || !this.formManager) return;

        const images = this.formManager.getImages();
        const tiles = images.map((image, index) => this.createImageElement(image, index));
        renderMediaTiles(this.elements.imageList, tiles);
    }

    public updateVideoDisplay(): void {
        if (!(this.elements.videoList instanceof HTMLElement) || !this.formManager) return;

        const videos = this.formManager.getVideos();
        const tiles = videos.map((video, index) => this.createVideoElement(video, index));
        renderMediaTiles(this.elements.videoList, tiles);
    }

    public createImageElement(image: PostCreateImageItem, index: number): HTMLElement {
        const tile = PostMediaRender.createImageElement(image, index) as HTMLElement;

        const removeBtn = postCreateForm.getMediaRemoveButton(tile);
        if (removeBtn) {
            PostCreateListener.bindClick(removeBtn, () => {
                this.formManager?.removeImage(image.id);
                this.updateImageDisplay();
            });
        }

        return tile;
    }

    public createVideoElement(video: PostCreateVideoItem, index: number): HTMLElement {
        const tile = PostMediaRender.createVideoElement(video, index) as HTMLElement;

        const removeBtn = postCreateForm.getMediaRemoveButton(tile);
        if (removeBtn) {
            PostCreateListener.bindClick(removeBtn, (event) => {
                event.preventDefault();
                this.formManager?.removeVideo(video.id);
                this.updateVideoDisplay();
            });
        }

        return tile;
    }

    public updateTranslationUI(_enabled: boolean): void {
        return;
    }

    public updateRatingDisplay(_rating: number): void {
        return;
    }

    public updateSnsPreviewInTab(): void {
        return;
    }

    public updateOgpPreviewInTab(): void {
        const root = postCreateForm.getPostCreateForm(this.elements?.form || document);
        const searchRoot = root || document;

        const previewTabContainer = postCreateForm.getOgpPreviewTab(
            postCreateForm.getOgpPreviewTab(searchRoot) || document,
        );
        if (!previewTabContainer) return;

        const contentTextarea = postCreateForm.getContentField(searchRoot);
        if (!contentTextarea) return;

        PostSharedPreviewTabUtils.updateOgpPreviewInTab({
            contentTextarea,
            previewTabContainer,
        });
    }

    public getTranslation(key: string, fallback: string): string {
        try {
            if (translator && typeof translator.getTranslation === "function") {
                return translator.getTranslation(key) || fallback;
            }
        } catch (error) {
            console.error("Translation failed for key:", key, error);
        }

        return fallback;
    }

    private getFilesFromEvent(event: Event): File[] {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.files) {
            return [];
        }

        return Array.from(target.files);
    }

    private createFileEvent(files: File[]): Event {
        return {
            target: {
                files,
                value: "",
            },
        } as unknown as Event;
    }
}

const postCreateHandler = new PostCreateHandler();

export { PostCreateHandler, postCreateHandler };
